/**
 * SubscriptionAccordion Component
 * Accordion section for each subscription type
 */

import React, { useMemo } from 'react';
import { Subscription, SubscriptionTypeMetadata, SubscriptionType } from '../types/SubscriptionTypes';
import { SubscriptionItem } from './SubscriptionItem';
import './SubscriptionAccordion.css';

interface SubscriptionAccordionProps {
  type: SubscriptionType;
  subscriptions: Subscription[];
  metadata: SubscriptionTypeMetadata;
  isExpanded: boolean;
  onToggle: () => void;
  onSubscriptionUpdate?: (subscriptionId: string) => void;
}

export const SubscriptionAccordion: React.FC<SubscriptionAccordionProps> = ({
  type,
  subscriptions,
  metadata,
  isExpanded,
  onToggle,
  onSubscriptionUpdate
}) => {
  // Count active subscriptions
  const activeCount = useMemo(() => subscriptions.filter(s => s.status === 'active').length, [subscriptions]);

  return (
    <div className={`subscription-accordion accordion-${type}`}>
      <button
        className={`accordion-header ${isExpanded ? 'expanded' : ''}`}
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={`accordion-content-${type}`}
      >
        <span className="accordion-icon">
          <span className="icon-emoji">{metadata.icon}</span>
        </span>

        <span className="accordion-title">
          <h3>{metadata.label}</h3>
          <p className="accordion-description">{metadata.description}</p>
        </span>

        <span className="accordion-badge">
          {activeCount}/{subscriptions.length} active
        </span>

        <span className={`accordion-chevron ${isExpanded ? 'rotated' : ''}`}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor">
            <polyline points="6 9 10 13 14 9"></polyline>
          </svg>
        </span>
      </button>

      {isExpanded && (
        <div className="accordion-content" id={`accordion-content-${type}`}>
          <div className="subscriptions-list">
            {subscriptions.map(subscription => (
              <SubscriptionItem
                key={subscription.id}
                subscription={subscription}
                onSubscriptionUpdate={onSubscriptionUpdate}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionAccordion;
